import { useState } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { Sparkles, Check, AlertCircle } from 'lucide-react';
import { db } from '../../lib/firebase';
import { Organization } from '../../types/index';

interface FeatureFlagsCardProps {
  organization: Organization;
  onUpdate: (updated: Organization) => void;
}

interface FlagConfig {
  key: keyof NonNullable<Organization['features']>;
  label: string;
  description: string;
}

const FLAGS: FlagConfig[] = [
  {
    key: 'socialImport',
    label: 'Import IA (Social Import)',
    description: 'Permet d\'importer des recettes depuis TikTok / Instagram via l\'IA. Visible dans l\'éditeur de recette.',
  },
];

type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

export function FeatureFlagsCard({ organization, onUpdate }: FeatureFlagsCardProps) {
  const [saveStatus, setSaveStatus] = useState<Record<string, SaveStatus>>({});

  const getFlag = (key: string): boolean =>
    (organization.features as Record<string, boolean> | undefined)?.[key] === true;

  const handleToggle = async (key: string, newValue: boolean) => {
    setSaveStatus((s) => ({ ...s, [key]: 'saving' }));
    try {
      const orgRef = doc(db, 'organizations', organization.id);
      await updateDoc(orgRef, { [`features.${key}`]: newValue });

      onUpdate({
        ...organization,
        features: { ...organization.features, [key]: newValue },
      });

      setSaveStatus((s) => ({ ...s, [key]: 'saved' }));
      setTimeout(() => setSaveStatus((s) => ({ ...s, [key]: 'idle' })), 2000);
    } catch (err) {
      console.error('Error updating feature flag:', err);
      setSaveStatus((s) => ({ ...s, [key]: 'error' }));
      setTimeout(() => setSaveStatus((s) => ({ ...s, [key]: 'idle' })), 3000);
    }
  };

  return (
    <div className="bg-white shadow-sm rounded-xl p-4 sm:p-6 border border-gray-100">
      <div className="flex items-center space-x-3 mb-6">
        <Sparkles className="h-6 w-6 text-menioo-green" />
        <h2 className="text-xl font-display font-bold text-menioo-navy">
          Feature Flags
        </h2>
      </div>

      <div className="space-y-4">
        {FLAGS.map(({ key, label, description }) => {
          const enabled = getFlag(key);
          const status = saveStatus[key] ?? 'idle';
          const isSaving = status === 'saving';

          return (
            <div
              key={key}
              className="flex items-start justify-between gap-4 p-4 rounded-lg border border-gray-100 bg-gray-50"
            >
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-menioo-navy">{label}</p>
                <p className="mt-0.5 text-xs text-gray-500">{description}</p>
              </div>

              <div className="flex items-center gap-2 shrink-0">
                {status === 'saved' && (
                  <span className="flex items-center gap-1 text-xs text-menioo-green font-medium">
                    <Check className="h-3.5 w-3.5" /> Sauvegardé
                  </span>
                )}
                {status === 'error' && (
                  <span className="flex items-center gap-1 text-xs text-red-500 font-medium">
                    <AlertCircle className="h-3.5 w-3.5" /> Erreur
                  </span>
                )}

                <button
                  type="button"
                  role="switch"
                  aria-checked={enabled}
                  disabled={isSaving}
                  onClick={() => handleToggle(key, !enabled)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-menioo-green focus:ring-offset-2 disabled:opacity-50 ${
                    enabled ? 'bg-menioo-green' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                      enabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
